import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode as QrCodeIcon, Plus, ExternalLink } from 'lucide-react';
import {
  useToast,
  PageHeader, Button, Input, EmptyState,
} from '../components/ui';

const QRCodePage = () => {
  const { toast } = useToast();
  const [tables, setTables] = useState([]);
  const [tableNumber, setTableNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchTables(); }, []);

  const fetchTables = async () => {
    try {
      const res = await axios.get('/api/qrcode', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setTables(res.data);
    } catch (err) { console.error(err); }
  };

  const generateQR = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!tableNumber) return;
    setSubmitting(true);
    try {
      await axios.post('/api/qrcode', { tableNumber }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setTableNumber('');
      fetchTables();
      toast.success('QR code generated');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error generating QR code');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="QR Code Ordering"
        subtitle="Generate scannable codes so customers can order from their table."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* QR codes grid */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-800">Active QR codes</h2>
            <span className="text-xs text-neutral-500">
              {tables.length} code{tables.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="p-5">
            {tables.length === 0 ? (
              <EmptyState
                icon={QrCodeIcon}
                title="No QR codes yet"
                message="Generate your first table QR code using the form on the right."
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {tables.map(table => {
                  const orderUrl = `${window.location.origin}/order/${table.code}`;
                  return (
                    <div
                      key={table._id}
                      className="bg-white border border-neutral-200 rounded-lg p-4 hover:shadow-card-hover transition-shadow flex flex-col items-center"
                    >
                      <div className="p-3 bg-white rounded-md border border-neutral-100">
                        <QRCodeSVG value={orderUrl} size={120} />
                      </div>
                      <p className="mt-3 text-sm font-semibold text-neutral-900">Table {table.tableNumber}</p>
                      <a
                        href={orderUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 text-[11px] text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        Open
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Generate form */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden h-fit">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <Plus size={16} className="text-primary-600" />
            <h2 className="text-sm font-semibold text-neutral-800">Generate new QR code</h2>
          </div>
          <form onSubmit={generateQR} className="p-5 space-y-3">
            <Input
              label="Table number"
              type="number"
              placeholder="e.g. 5"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              required
              helper="Each table can have one active QR code at a time."
            />
            <div className="pt-1">
              <Button type="submit" variant="primary" fullWidth loading={submitting} disabled={!tableNumber}>
                Generate QR
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default QRCodePage;
